import { useTable, usePagination, useSortBy, Column } from 'react-table'
import { useNavigate } from 'react-router-dom'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TablePagination from './table-pagination'

interface Customer {
  id: string
  name: string
  [key: string]: unknown
}

interface ReactTableProps {
  data: Customer[]
  columns: Column<Customer>[]
  searchQuery: string
  onSearchChange: (query: string) => void
  isSearchLoading?: boolean
}

const ReactTable = ({
  data,
  columns,
  searchQuery,
  onSearchChange,
  isSearchLoading,
}: ReactTableProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 25 },
    },
    useSortBy,
    usePagination
  )

  const navigate = useNavigate()

  const handleClickOnRow = (customer: Customer) => {
    navigate(`/customer/${customer.id}`)
  }

  const pageCount = Math.ceil(rows.length / pageSize)

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            All Customers
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              mt: 0.25,
            }}
          >
            Manage and monitor customer accounts
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search Bar */}
          <TextField
            size="small"
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              width: 280,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 13,
                '& fieldset': {
                  borderColor: 'divider',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.300',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: isSearchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
        <Table sx={{ minWidth: 650 }} {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps()
              return (
                <TableRow key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } = column.getHeaderProps()
                    return (
                      <TableCell
                        key={columnKey}
                        {...columnProps}
                        sx={{
                          minWidth: `${column.minWidth}px`,
                          py: 1.5,
                          px: 2.5,
                          bgcolor: 'action.hover',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box {...column.getSortByToggleProps()}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              color: 'text.secondary',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column.render('Header')}
                          </Typography>
                        </Box>
                      </TableCell>
                    )
                  })}
                  {/* Actions column header */}
                  <TableCell
                    sx={{
                      width: 50,
                      py: 1.5,
                      px: 2.5,
                      bgcolor: 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </TableRow>
              )
            })}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row)
              const { key: rowKey, ...rowProps } = row.getRowProps()
              return (
                <TableRow
                  key={rowKey}
                  {...rowProps}
                  onClick={() => handleClickOnRow(row.original)}
                  sx={{
                    bgcolor: 'background.paper',
                    '&:hover': {
                      cursor: 'pointer',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(84, 189, 203, 0.08)'
                          : 'primary.50',
                    },
                  }}
                >
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps()
                    return (
                      <TableCell
                        key={cellKey}
                        {...cellProps}
                        sx={{
                          py: 1.75,
                          px: 2.5,
                          fontSize: 14,
                          color: 'text.primary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    )
                  })}
                  {/* Actions cell */}
                  <TableCell
                    sx={{
                      py: 1.75,
                      px: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: 'text.disabled',
                        '&:hover': { color: 'text.secondary' },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        totalRows={rows.length}
        pageSize={pageSize}
        gotoPage={gotoPage}
      />
    </Paper>
  )
}

export default ReactTable
